import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea } from "@/components/voice-field";
import {
  Mail, FileText, AlertCircle, Send, Save, Trash2, Loader2,
  Reply, ReplyAll, Forward as ForwardIcon, PenSquare, X, Eye, Code2,
} from "lucide-react";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { sanitizeEmailHtml } from "@/lib/sanitize-html";

type GmailListItem = {
  id: string;
  threadId: string;
  subject: string;
  from?: string;
  to?: string;
  snippet: string;
  date: string | null;
  unread?: boolean;
};

type GmailDraftItem = {
  draftId: string;
  messageId: string;
  threadId: string;
  subject: string;
  to: string;
  snippet: string;
  date: string | null;
};

type GmailDraftDetail = {
  draftId: string;
  messageId: string;
  threadId: string;
  subject: string;
  to: string;
  cc: string;
  bcc: string;
  from: string;
  date: string | null;
  bodyHtml: string;
  bodyPlainText: string;
};

type SentMessage = {
  id: string;
  threadId: string;
  subject: string;
  from: string;
  to: string;
  cc: string;
  date: string | null;
  bodyHtml: string;
  bodyPlainText: string;
};

type FolderKind = "sent" | "drafts";

interface Props {
  folder: FolderKind;
}

function formatDate(d: string | null): string {
  if (!d) return "";
  try {
    return format(new Date(d), "MMM d");
  } catch {
    return "";
  }
}

function stripAngle(addr: string): string {
  const m = addr.match(/^([^<]+)<([^>]+)>$/);
  if (m) return m[1].trim().replace(/^["']|["']$/g, "") || m[2].trim();
  return addr;
}

function htmlToPlain(html: string): string {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>(\r?\n)?/gi, "\n")
    .replace(/<\/(p|div|h[1-6]|li)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function buildForwardQuote(msg: SentMessage): string {
  const sentAtStr = msg.date ? new Date(msg.date).toLocaleString() : "";
  const safeBody = msg.bodyHtml
    ? DOMPurify.sanitize(msg.bodyHtml, { USE_PROFILES: { html: true }, FORBID_TAGS: ["style", "iframe", "object", "embed"] })
    : `<pre style="white-space:pre-wrap;font-family:inherit;">${(msg.bodyPlainText || "").replace(/[<>&]/g, (c) => ({"<":"&lt;",">":"&gt;","&":"&amp;"}[c] || c))}</pre>`;
  return [
    "<p></p>",
    "<div style=\"border-left:2px solid #ccc;padding-left:12px;margin-top:8px;\">",
    "<p style=\"margin:0 0 8px 0;color:#555;\">---------- Forwarded message ----------</p>",
    `<p style="margin:0;color:#555;"><strong>From:</strong> ${msg.from || ""}</p>`,
    sentAtStr ? `<p style="margin:0;color:#555;"><strong>Date:</strong> ${sentAtStr}</p>` : "",
    `<p style="margin:0;color:#555;"><strong>Subject:</strong> ${msg.subject || ""}</p>`,
    msg.to ? `<p style="margin:0 0 8px 0;color:#555;"><strong>To:</strong> ${msg.to}</p>` : "",
    safeBody,
    "</div>",
  ].filter(Boolean).join("");
}

export function GmailFolderView({ folder }: Props) {
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newComposeOpen, setNewComposeOpen] = useState(false);

  const listQuery = useQuery<{ messages?: GmailListItem[]; drafts?: GmailDraftItem[] }>({
    queryKey: [folder === "sent" ? "/api/gmail/sent" : "/api/gmail/drafts"],
    retry: false,
  });

  const items: Array<{ id: string; messageId: string; subject: string; counterparty: string; snippet: string; date: string | null }> =
    folder === "sent"
      ? (listQuery.data?.messages || []).map((m) => ({
          id: m.id,
          messageId: m.id,
          subject: m.subject || "(no subject)",
          counterparty: m.to ? `To: ${stripAngle(m.to)}` : "",
          snippet: m.snippet,
          date: m.date,
        }))
      : (listQuery.data?.drafts || []).map((d) => ({
          id: d.draftId,
          messageId: d.messageId,
          subject: d.subject || "(no subject)",
          counterparty: d.to ? `To: ${stripAngle(d.to)}` : "(no recipient)",
          snippet: d.snippet,
          date: d.date,
        }));

  const selectedItem = items.find((i) => i.id === selectedId) || null;

  const listError = listQuery.error as any;
  const listReconnect = listError?.message?.includes("not connected") || listError?.status === 401 || listError?.status === 403;

  const handleSelectItem = (id: string) => {
    setSelectedId(id);
    setNewComposeOpen(false);
  };

  const handleNewCompose = () => {
    setSelectedId(null);
    setNewComposeOpen(true);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 h-full">
      <Card className="flex flex-col overflow-hidden" data-testid={`folder-list-${folder}`}>
        <div className="px-4 py-3 border-b flex items-center gap-2">
          {folder === "sent" ? <Mail className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
          <span className="font-semibold capitalize">{folder}</span>
          {!listQuery.isLoading && (
            <span className="text-xs text-muted-foreground ml-auto">{items.length}</span>
          )}
          <Button
            size="sm"
            variant="outline"
            className="ml-2 h-7 px-2 text-xs"
            onClick={handleNewCompose}
            data-testid="button-new-compose"
          >
            <PenSquare className="w-3.5 h-3.5 mr-1" />
            New email
          </Button>
        </div>
        <ScrollArea className="flex-1">
          {listQuery.isLoading ? (
            <div className="p-4 space-y-3">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : listReconnect ? (
            <div className="p-6 text-sm text-muted-foreground flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>Connect your Gmail in Settings to view {folder}.</div>
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              No {folder === "sent" ? "sent messages" : "drafts"} yet.
            </div>
          ) : (
            <div className="divide-y">
              {items.map((it) => (
                <button
                  key={it.id}
                  data-testid={`${folder}-item-${it.id}`}
                  onClick={() => handleSelectItem(it.id)}
                  className={`w-full text-left px-4 py-3 hover-elevate active-elevate-2 ${
                    selectedId === it.id ? "bg-muted/60" : ""
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs text-muted-foreground truncate flex-1">{it.counterparty}</span>
                    <span className="text-[11px] text-muted-foreground shrink-0">{formatDate(it.date)}</span>
                  </div>
                  <div className="text-sm font-medium truncate">{it.subject}</div>
                  <div className="text-xs text-muted-foreground truncate">{it.snippet}</div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </Card>

      <Card className="flex flex-col overflow-hidden" data-testid={`folder-detail-${folder}`}>
        {newComposeOpen ? (
          <ComposePanel
            onClose={() => setNewComposeOpen(false)}
            onSent={() => {
              setNewComposeOpen(false);
              queryClient.invalidateQueries({ queryKey: ["/api/gmail/sent"] });
            }}
            toast={toast}
          />
        ) : !selectedItem ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <PenSquare className="w-8 h-8 opacity-30" />
            <div className="text-sm text-center">
              <p>Select a {folder === "sent" ? "message" : "draft"} to view it</p>
              <p className="text-xs mt-1 opacity-70">or click <strong>New email</strong> to compose</p>
            </div>
          </div>
        ) : folder === "sent" ? (
          <SentDetail
            messageId={selectedItem.messageId}
            toast={toast}
          />
        ) : (
          <DraftEditor
            draftId={selectedItem.id}
            onClose={() => setSelectedId(null)}
            toast={toast}
          />
        )}
      </Card>
    </div>
  );
}

// ─── Sent detail with Reply / Reply-All / Forward ─────────────────────────────

type ComposeMode = "reply" | "replyAll" | "forward" | null;

function SentDetail({
  messageId,
  toast,
}: {
  messageId: string;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const detailQuery = useQuery<{ message: SentMessage }>({
    queryKey: ["/api/gmail/messages", messageId],
    enabled: !!messageId,
    retry: false,
  });

  const [composeMode, setComposeMode] = useState<ComposeMode>(null);
  const [composeBody, setComposeBody] = useState("");
  const [forwardTo, setForwardTo] = useState("");
  const [composeCc, setComposeCc] = useState("");
  const [isPreviewCompose, setIsPreviewCompose] = useState(false);

  const resetCompose = () => {
    setComposeMode(null);
    setComposeBody("");
    setForwardTo("");
    setComposeCc("");
    setIsPreviewCompose(false);
  };

  const replyMutation = useMutation({
    mutationFn: async () => {
      const msg = detailQuery.data?.message;
      if (!msg) throw new Error("Message not loaded");
      const toAddr = stripAngle(msg.to);
      const cc = composeMode === "replyAll" && msg.cc
        ? msg.cc.split(",").map((s) => s.trim()).filter(Boolean).join(", ")
        : composeCc;
      return apiRequest("POST", `/api/gmail/messages/${messageId}/reply`, {
        to: toAddr,
        body: composeBody,
        cc: cc || undefined,
      });
    },
    onSuccess: () => {
      toast({ title: "Reply sent" });
      resetCompose();
      queryClient.invalidateQueries({ queryKey: ["/api/gmail/sent"] });
    },
    onError: (err: any) => {
      toast({ title: "Reply failed", description: err?.message || "Could not send reply", variant: "destructive" });
    },
  });

  const forwardMutation = useMutation({
    mutationFn: async () => {
      if (!forwardTo.trim()) throw new Error("Recipient required");
      return apiRequest("POST", `/api/gmail/messages/${messageId}/forward`, {
        to: forwardTo.trim(),
        body: composeBody,
        cc: composeCc || undefined,
      });
    },
    onSuccess: () => {
      toast({ title: "Message forwarded" });
      resetCompose();
      queryClient.invalidateQueries({ queryKey: ["/api/gmail/sent"] });
    },
    onError: (err: any) => {
      toast({ title: "Forward failed", description: err?.message || "Could not forward", variant: "destructive" });
    },
  });

  const handleReply = (all = false) => {
    const msg = detailQuery.data?.message;
    resetCompose();
    setComposeMode(all ? "replyAll" : "reply");
    if (all && msg?.cc) setComposeCc(msg.cc);
    setComposeBody("");
  };

  const handleForward = () => {
    const msg = detailQuery.data?.message;
    if (!msg) return;
    resetCompose();
    setComposeMode("forward");
    setComposeBody(buildForwardQuote(msg));
    setIsPreviewCompose(true);
  };

  const handleSend = () => {
    if (composeMode === "forward") {
      if (!forwardTo.trim()) {
        toast({ title: "Recipient required", description: "Enter a To address before forwarding.", variant: "destructive" });
        return;
      }
      forwardMutation.mutate();
    } else {
      replyMutation.mutate();
    }
  };

  const busy = replyMutation.isPending || forwardMutation.isPending;

  if (detailQuery.isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (detailQuery.error) {
    return <div className="p-6 text-sm text-destructive">Failed to load message.</div>;
  }
  const msg = detailQuery.data?.message;
  if (!msg) return null;

  const replyToDisplay = stripAngle(msg.to);

  return (
    <div className="flex flex-col h-full">
      {/* Header + action buttons */}
      <div className="px-6 py-4 border-b space-y-3 shrink-0">
        <div className="space-y-1">
          <div className="text-lg font-semibold" data-testid="text-message-subject">
            {msg.subject || "(no subject)"}
          </div>
          <div className="text-xs text-muted-foreground">
            To: {replyToDisplay}
            {msg.cc ? ` · Cc: ${stripAngle(msg.cc)}` : ""}
            {msg.date ? ` · ${formatDate(msg.date)}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleReply(false)}
            disabled={!!composeMode}
            data-testid="button-reply"
          >
            <Reply className="w-4 h-4 mr-1" />
            Reply
          </Button>
          {msg.cc && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleReply(true)}
              disabled={!!composeMode}
              data-testid="button-reply-all"
            >
              <ReplyAll className="w-4 h-4 mr-1" />
              Reply all
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleForward}
            disabled={!!composeMode}
            data-testid="button-forward"
          >
            <ForwardIcon className="w-4 h-4 mr-1" />
            Forward
          </Button>
        </div>
      </div>

      {/* Original message body */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          {msg.bodyHtml ? (
            <div
              className="prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(msg.bodyHtml, {
                  USE_PROFILES: { html: true },
                  FORBID_TAGS: ["style", "iframe", "object", "embed", "base", "meta", "form"],
                }),
              }}
            />
          ) : (
            <pre className="whitespace-pre-wrap text-sm">{msg.bodyPlainText}</pre>
          )}
        </div>
      </ScrollArea>

      {/* Inline compose panel */}
      {composeMode && (
        <div className="border-t bg-muted/30 px-6 py-4 space-y-3 shrink-0">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {composeMode === "forward" ? "Forward" : composeMode === "replyAll" ? "Reply all" : `Reply to ${replyToDisplay}`}
            </span>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={resetCompose} data-testid="button-cancel-compose">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {composeMode === "forward" && (
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input
                value={forwardTo}
                onChange={(e) => setForwardTo(e.target.value)}
                placeholder="recipient@example.com"
                className="h-8 text-sm"
                data-testid="input-forward-to"
              />
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Cc (optional)</Label>
            <Input
              value={composeCc}
              onChange={(e) => setComposeCc(e.target.value)}
              placeholder="cc@example.com"
              className="h-8 text-sm"
              data-testid="input-compose-cc"
            />
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Message</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-muted-foreground"
                onClick={() => setIsPreviewCompose((v) => !v)}
                data-testid="button-toggle-compose-preview"
              >
                {isPreviewCompose ? <><Code2 className="w-3 h-3 mr-1" /> Edit HTML</> : <><Eye className="w-3 h-3 mr-1" /> Preview</>}
              </Button>
            </div>
            {isPreviewCompose ? (
              <div
                className="min-h-[8rem] max-h-64 overflow-y-auto rounded-md border bg-white dark:bg-zinc-950 p-3 prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(composeBody) }}
                data-testid="preview-compose-body"
              />
            ) : (
              <VoiceToTextTextarea
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                rows={composeMode === "forward" ? 6 : 5}
                placeholder={composeMode === "forward" ? "Add a note above the forwarded message…" : "Write your reply…"}
                className="font-mono text-xs"
                data-testid="textarea-compose-body"
              />
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={resetCompose} disabled={busy} data-testid="button-compose-cancel">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSend}
              disabled={busy || (!composeBody.trim() && composeMode !== "forward")}
              data-testid="button-compose-send"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</>
              ) : (
                <><Send className="w-4 h-4 mr-1" /> Send</>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── New email compose panel ──────────────────────────────────────────────────

function ComposePanel({
  onClose,
  onSent,
  toast,
}: {
  onClose: () => void;
  onSent: () => void;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);

  const sendMutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", "/api/gmail/messages/compose", { to, cc, bcc, subject, body }),
    onSuccess: () => {
      toast({ title: "Sent", description: "Your email was sent." });
      onSent();
    },
    onError: (err: any) => {
      toast({ title: "Send failed", description: err?.message || "Could not send email", variant: "destructive" });
    },
  });

  return (
    <div className="flex flex-col h-full" data-testid="component-compose-panel">
      <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
        <div className="text-lg font-semibold">New email</div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="button-close-compose">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="compose-to">To</Label>
            <Input
              id="compose-to"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              data-testid="input-compose-to"
            />
          </div>
          {!showCcBcc ? (
            <button
              type="button"
              onClick={() => setShowCcBcc(true)}
              className="text-xs text-muted-foreground hover:text-foreground"
              data-testid="button-show-cc-bcc"
            >
              Add Cc/Bcc
            </button>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="compose-cc">Cc</Label>
                <Input id="compose-cc" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="input-compose-cc" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="compose-bcc">Bcc</Label>
                <Input id="compose-bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} data-testid="input-compose-bcc" />
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="compose-subject">Subject</Label>
            <Input
              id="compose-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="input-compose-subject"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="compose-body">Body</Label>
            <VoiceToTextTextarea
              id="compose-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              placeholder="Write your message…"
              data-testid="input-compose-body"
            />
          </div>
        </div>
      </ScrollArea>
      <div className="px-6 py-3 border-t flex items-center justify-end gap-2 shrink-0">
        <Button variant="outline" size="sm" onClick={onClose} data-testid="button-compose-discard">
          Discard
        </Button>
        <Button
          size="sm"
          onClick={() => sendMutation.mutate()}
          disabled={sendMutation.isPending || !to.trim() || !body.trim()}
          data-testid="button-compose-send"
        >
          {sendMutation.isPending ? (
            <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</>
          ) : (
            <><Send className="w-4 h-4 mr-1" /> Send</>
          )}
        </Button>
      </div>
    </div>
  );
}

// ─── Draft editor with HTML preview toggle ────────────────────────────────────

function DraftEditor({
  draftId,
  onClose,
  toast,
}: {
  draftId: string;
  onClose: () => void;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const detailQuery = useQuery<{ draft: GmailDraftDetail }>({
    queryKey: ["/api/gmail/drafts", draftId],
    enabled: !!draftId,
    retry: false,
  });

  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [isPreviewBody, setIsPreviewBody] = useState(false);

  useEffect(() => {
    const d = detailQuery.data?.draft;
    if (d) {
      setTo(d.to || "");
      setCc(d.cc || "");
      setBcc(d.bcc || "");
      setSubject(d.subject || "");
      // Prefer HTML so preview mode renders correctly; fallback to plain text.
      setBody(d.bodyHtml || d.bodyPlainText || "");
      setShowCcBcc(!!(d.cc || d.bcc));
      // Default to preview when there's HTML content to show.
      setIsPreviewBody(!!(d.bodyHtml));
    }
  }, [detailQuery.data?.draft]);

  const refreshAfterMutation = (alsoSent = false) => {
    queryClient.invalidateQueries({ queryKey: ["/api/gmail/drafts"] });
    if (alsoSent) queryClient.invalidateQueries({ queryKey: ["/api/gmail/sent"] });
  };

  const saveDraft = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", `/api/gmail/drafts/${draftId}`, {
        to, cc, bcc, subject,
        body: "",
        bodyHtml: body,
      });
      return res.json();
    },
    onSuccess: () => {
      refreshAfterMutation();
      queryClient.invalidateQueries({ queryKey: ["/api/gmail/drafts", draftId] });
      toast({ title: "Draft saved" });
    },
    onError: (err: any) => {
      toast({ title: "Save failed", description: err?.message || "Could not save draft", variant: "destructive" });
    },
  });

  const sendDraft = useMutation({
    mutationFn: async () => {
      await apiRequest("PUT", `/api/gmail/drafts/${draftId}`, {
        to, cc, bcc, subject,
        body: "",
        bodyHtml: body,
      });
      const res = await apiRequest("POST", `/api/gmail/drafts/${draftId}/send`);
      return res.json();
    },
    onSuccess: () => {
      refreshAfterMutation(true);
      toast({ title: "Sent", description: "Your draft was sent." });
      onClose();
    },
    onError: (err: any) => {
      toast({ title: "Send failed", description: err?.message || "Could not send draft", variant: "destructive" });
    },
  });

  const discardDraft = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/gmail/drafts/${draftId}`);
      return res.json();
    },
    onSuccess: () => {
      refreshAfterMutation();
      toast({ title: "Draft discarded" });
      onClose();
    },
    onError: (err: any) => {
      toast({ title: "Discard failed", description: err?.message || "Could not delete draft", variant: "destructive" });
    },
  });

  if (detailQuery.isLoading) {
    return (
      <div className="p-6 space-y-3" data-testid="status-loading-draft-detail">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const err = detailQuery.error as any;
  const reconnect = err?.message?.includes("not connected") || err?.status === 401 || err?.status === 403;
  if (reconnect) {
    return (
      <div className="p-6 text-sm text-muted-foreground flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>Connect your Gmail in Settings to edit this draft.</div>
      </div>
    );
  }
  if (err || !detailQuery.data?.draft) {
    return <div className="p-6 text-sm text-destructive">Failed to load draft.</div>;
  }

  const busy = saveDraft.isPending || sendDraft.isPending || discardDraft.isPending;

  const onDiscard = () => {
    if (window.confirm("Discard this draft? This cannot be undone.")) {
      discardDraft.mutate();
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="component-gmail-draft-editor">
      <div className="px-6 py-4 border-b shrink-0">
        <div className="text-lg font-semibold">Edit draft</div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`draft-to-${draftId}`}>To</Label>
            <Input
              id={`draft-to-${draftId}`}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              data-testid="input-draft-to"
            />
          </div>
          {!showCcBcc ? (
            <button
              type="button"
              onClick={() => setShowCcBcc(true)}
              className="text-xs text-muted-foreground hover:text-foreground"
              data-testid="button-show-cc-bcc"
            >
              Add Cc/Bcc
            </button>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor={`draft-cc-${draftId}`}>Cc</Label>
                <Input
                  id={`draft-cc-${draftId}`}
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  data-testid="input-draft-cc"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`draft-bcc-${draftId}`}>Bcc</Label>
                <Input
                  id={`draft-bcc-${draftId}`}
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  data-testid="input-draft-bcc"
                />
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label htmlFor={`draft-subject-${draftId}`}>Subject</Label>
            <Input
              id={`draft-subject-${draftId}`}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="input-draft-subject"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor={`draft-body-${draftId}`}>Body</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground"
                onClick={() => setIsPreviewBody((v) => !v)}
                data-testid="button-toggle-draft-body"
              >
                {isPreviewBody ? (
                  <><Code2 className="w-3.5 h-3.5 mr-1" /> Edit HTML</>
                ) : (
                  <><Eye className="w-3.5 h-3.5 mr-1" /> Preview</>
                )}
              </Button>
            </div>
            {isPreviewBody ? (
              <div
                className="min-h-[18rem] rounded-lg border bg-white dark:bg-zinc-950 p-4 overflow-y-auto prose prose-sm max-w-none dark:prose-invert [&_a]:text-blue-600 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(body) }}
                data-testid="preview-draft-body"
              />
            ) : (
              <VoiceToTextTextarea
                id={`draft-body-${draftId}`}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={14}
                className="font-mono text-sm"
                data-testid="input-draft-body"
              />
            )}
          </div>
        </div>
      </ScrollArea>
      <div className="px-6 py-3 border-t flex items-center justify-end gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={onDiscard}
          disabled={busy}
          data-testid="button-draft-discard"
        >
          <Trash2 className="w-4 h-4 mr-1" /> Discard
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => saveDraft.mutate()}
          disabled={busy}
          data-testid="button-draft-save"
        >
          {saveDraft.isPending ? (
            <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
          ) : (
            <><Save className="w-4 h-4 mr-1" /> Save</>
          )}
        </Button>
        <Button
          size="sm"
          onClick={() => sendDraft.mutate()}
          disabled={busy || !to.trim()}
          data-testid="button-draft-send"
        >
          {sendDraft.isPending ? (
            <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</>
          ) : (
            <><Send className="w-4 h-4 mr-1" /> Send</>
          )}
        </Button>
      </div>
    </div>
  );
}
